import { useQuery } from '@tanstack/react-query';

import { assistantApi } from '@/modules/assistant/services/assistant-api';
import { assistantQueryKeys } from '@/modules/assistant/hooks/query-keys';
import type { AssistantScope } from '@/modules/assistant/services/assistant-scope';

export type AssistantContextOptions = {
  /**
   * Whether the context may be read.
   *
   * The panel asks for the context only once it is open: a read on every page
   * load would be a request per page for a panel nobody opened.
   */
  enabled?: boolean;
};

/**
 * The route-aware context for the page the caller is on.
 *
 * The route is part of the key, so a navigation to another page fetches that
 * page's skill and that page's suggestions instead of serving the previous page's.
 * The query is disabled for an empty route rather than sent as one: the API
 * refuses a route it cannot match, and the console has nothing to ask about.
 */
export function useAssistantContext(
  scope: AssistantScope,
  route: string,
  options: AssistantContextOptions = {},
) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: assistantQueryKeys.context(scope, route),
    queryFn: () => assistantApi.getContext(scope, { route }),
    enabled: enabled && route.length > 0,
  });
}
